/* ============================================================
   AM2T — cart-pack-engine.js
   Competition Pack + gift bottles, client side.

   IMPORTANT: everything here runs in the shopper's browser, so it is
   a convenience layer, not a security boundary. The plan is to move
   the pack to Shopify Bundles and the gifts to automatic "Buy X get
   Y" discounts (see the audit doc). When that is live in the admin,
   turn OFF "Client-side pack engine" in Theme settings → Competition
   Pack and this file stops doing anything.

   What it does today:
   1. Auto-adds the orange bottle (gift) when the cart has
      1+ pack-top + 1+ pack-bottom + 1+ pack-socks; removes it when the
      pack breaks.
   2. Auto-adds the grey bottle when the subtotal reaches the threshold
      and the pack is incomplete; removes it below the threshold.
   3. Appends ?discount=<code> to checkout links (and the cart page
      checkout button) while the pack is complete.
   4. Guard: refuses Ajax adds of "pro-athlete-only" variants that are
      not flagged as gifts (JSON, FormData and URL-encoded bodies).

   Reads ids from window.AM2T.packEligibleIds (snippets/pack-config)
   and settings from window.AM2T.pack (layout/theme.liquid).
   Depends on global.js.
   ============================================================ */

(() => {
  'use strict';

  const AM2T = window.AM2T;
  const settings = AM2T.pack || {};
  if (settings.engineEnabled === false) return;

  const DEBOUNCE_MS = 500;
  const discountCode = settings.discountCode || '';
  const routes = AM2T.routes;

  /* === 4. Guard for pro-athlete-only variants ====================== */
  const readItems = (body) => {
    if (!body) return [];
    if (typeof body === 'string') {
      try {
        const json = JSON.parse(body);
        return json.items ? json.items : json.id ? [json] : [];
      } catch {
        body = new URLSearchParams(body);
      }
    }
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const items = [];
      const id = body.get('id');
      if (id) items.push({ id, properties: { _gift: body.get('properties[_gift]') } });
      body.forEach((value, key) => {
        const m = key.match(/^items\[(\d+)\]\[id\]$/);
        if (m) items.push({ id: value, properties: { _gift: body.get(`items[${m[1]}][properties][_gift]`) } });
      });
      return items;
    }
    if (typeof body === 'object') return body.items ? body.items : body.id ? [body] : [];
    return [];
  };

  const blockedIn = (items) => {
    const blocked = AM2T.proAthleteOnlyVariantIds || [];
    return items.some((it) => {
      const id = typeof it.id === 'string' ? parseInt(it.id, 10) : it.id;
      const gift = it.properties && (it.properties._gift === 'true' || it.properties._gift === true);
      return blocked.includes(id) && !gift;
    });
  };

  const originalFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      if (url.indexOf('/cart/add') !== -1 && init && init.body && blockedIn(readItems(init.body))) {
        console.warn('[AM2T] blocked add of pro-athlete-only variant');
        return Promise.resolve(
          new Response(
            JSON.stringify({ status: 422, description: AM2T.t('pack_only_error') }),
            { status: 422, headers: { 'Content-Type': 'application/json' } }
          )
        );
      }
    } catch (e) { /* fail-open: never break legit requests */ }
    return originalFetch(input, init);
  };

  /* === 1–3. Pack engine ============================================ */
  class CartPackEngine {
    constructor() {
      this.syncing = false;
      this.timer = null;
      this.packComplete = false;

      document.addEventListener('cart:update', (e) => {
        if (e.detail?.source === 'cart-pack-engine') return; // ignore self
        this.scheduleSync();
      });

      // Capture phase: runs before the click navigates, and survives the
      // cart drawer being re-rendered (listener lives on document).
      document.addEventListener('click', (e) => this.onCheckoutClick(e), true);
      document.addEventListener('submit', (e) => this.onCartFormSubmit(e), true);

      // Initial check (the cart may already hold a pack).
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.scheduleSync(), { once: true });
      } else {
        this.scheduleSync();
      }
    }

    onCheckoutClick(e) {
      if (!discountCode) return;
      const link = e.target.closest ? e.target.closest('a[href*="/checkout"]') : null;
      if (!link) return;
      try {
        const url = new URL(link.getAttribute('href'), window.location.origin);
        if (this.packComplete) {
          url.searchParams.set('discount', discountCode);
        } else if (url.searchParams.get('discount') === discountCode) {
          url.searchParams.delete('discount');
        }
        link.setAttribute('href', url.pathname + url.search);
      } catch (err) {
        console.warn('[cart-pack-engine] could not update checkout link:', err);
      }
    }

    // Cart page: the "Checkout" submit button posts the form to /cart.
    // Save the quantities first, then go to checkout with the code.
    onCartFormSubmit(e) {
      if (!discountCode || !this.packComplete) return;
      const form = e.target;
      const submitter = e.submitter;
      if (!form || !submitter || submitter.name !== 'checkout') return;
      e.preventDefault();
      originalFetch(routes.cart_update + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      })
        .catch(() => {})
        .finally(() => {
          window.location.href = `/checkout?discount=${encodeURIComponent(discountCode)}`;
        });
    }

    scheduleSync() {
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(() => this.checkAndSync(), DEBOUNCE_MS);
    }

    async checkAndSync() {
      if (this.syncing) return;
      const ids = AM2T.packIds();
      if (!ids) {
        console.warn('[cart-pack-engine] window.AM2T.packEligibleIds not available');
        return;
      }

      this.syncing = true;
      try {
        const cart = await AM2T.cart.get();
        const items = cart.items || [];
        const state = AM2T.packState(cart);
        this.packComplete = state.complete;

        // Grey first, so a pack-complete transition removes grey before
        // orange is added. Never both at once: the pack wins.
        const greyId = ids.greyBottleVariantId;
        const greyItem = greyId ? items.find((i) => i.variant_id === greyId) : null;
        const greyThresholdCents = (ids.greyBottleThreshold || 0) * 100;
        // Gift lines don't count toward the threshold (a gift must never
        // unlock another gift).
        const subtotal = AM2T.paidSubtotal(cart);

        if (ids.greyBottleEnabled && greyId) {
          if (state.complete && greyItem) {
            await this.removeLine(greyItem.key);
          } else if (!state.complete && subtotal >= greyThresholdCents && !greyItem) {
            await this.addGift(greyId);
          } else if (!state.complete && subtotal < greyThresholdCents && greyItem) {
            await this.removeLine(greyItem.key);
          }
        }

        const orangeId = ids.orangeBottleVariantId;
        const orangeItem = orangeId ? items.find((i) => i.variant_id === orangeId) : null;
        if (state.complete && !orangeItem) {
          if (!orangeId) {
            console.warn(
              '[cart-pack-engine] no gift bottle found. Pick it in Theme settings → Competition Pack, ' +
              'and check it is published to the Online Store.'
            );
            return;
          }
          await this.addGift(orangeId);
        } else if (!state.complete && orangeItem) {
          await this.removeLine(orangeItem.key);
        }
      } catch (err) {
        console.warn('[cart-pack-engine] sync failed:', err);
      } finally {
        this.syncing = false;
      }
    }

    async addGift(variantId) {
      try {
        const cart = await AM2T.cart.add([
          { id: variantId, quantity: 1, properties: { _gift: 'true', _free: 'true' } }
        ]);
        AM2T.publishCart(cart, 'cart-pack-engine');
      } catch (err) {
        console.warn('[cart-pack-engine] add gift failed:', err);
      }
    }

    async removeLine(key) {
      if (!key) return;
      try {
        const cart = await AM2T.cart.change(key, 0);
        AM2T.publishCart(cart, 'cart-pack-engine');
      } catch (err) {
        console.warn('[cart-pack-engine] remove gift failed:', err);
      }
    }
  }

  new CartPackEngine();
})();
